import { useFlag } from '@automata/utils/hooks';
import { Box, Typography } from '@mui/material';
import { FC, useCallback, useMemo } from 'react';

import { FormFieldRow } from './FormFieldRow';
import { InstrumentAccordion } from './InstrumentAccordion';
import { SimulateField } from './SimulateField';
import { FieldValue, WorkcellConfig } from './types';
import { selectDriversByInstrumentId } from '../../features/workflow/selectors';
import { useAppSelector } from '../../hooks';

interface InstrumentSectionProps {
  errors: Record<string, Record<string, string | null> | null>;
  formValues: WorkcellConfig;
  instrument: WorkcellConfig[string];
  name: string;
  isFieldChanged: (instrument: string, key: string) => boolean;
  isSimulateFieldChanged: (instrument: string) => boolean;
  isInstrumentChanged: (instrument: string) => boolean;
  resetField: (instrument: string, key: string) => void;
  validateAndUpdate: (
    instrument: string,
    key: string,
    value: FieldValue
  ) => void;
  updateSimulateField: (instrument: string, value: boolean) => void;
}

export const InstrumentSection: FC<InstrumentSectionProps> = ({
  errors,
  formValues,
  instrument,
  name,
  isFieldChanged,
  isSimulateFieldChanged,
  isInstrumentChanged,
  resetField,
  validateAndUpdate,
  updateSimulateField,
}) => {
  const enableSimulateInstruments = useFlag('fe.enable_simulate_instruments');

  const driversByInstrumentId = useAppSelector(selectDriversByInstrumentId);
  const driverSchema = useMemo(() => {
    return driversByInstrumentId[name]?.configuration ?? {};
  }, [driversByInstrumentId, name]);

  const renderField = useCallback(
    (key: string) => {
      const value = formValues[name]?.config?.[key];
      const configMeta = driverSchema[key];

      return (
        <FormFieldRow
          key={key}
          instrumentName={name}
          fieldKey={key}
          value={value as FieldValue}
          configMeta={configMeta}
          error={errors?.[name]?.[key]}
          changed={isFieldChanged(name, key)}
          onChange={validateAndUpdate}
          onReset={resetField}
        />
      );
    },
    [
      driverSchema,
      errors,
      formValues,
      name,
      isFieldChanged,
      validateAndUpdate,
      resetField,
    ]
  );

  return (
    <InstrumentAccordion
      iconType={instrument.driverName ?? ''}
      title={
        <Box
          display="flex"
          alignItems="center"
          justifyContent="space-between"
          sx={{ marginRight: '73px' }}
        >
          {name}
          {isInstrumentChanged(name) && (
            <Typography color="error" fontSize={14} sx={{ ml: 1 }}>
              ●
            </Typography>
          )}
        </Box>
      }
    >
      {enableSimulateInstruments && (
        <SimulateField
          isSimulateFieldChanged={isSimulateFieldChanged}
          value={formValues[name]?.simulate ?? false}
          isInvalid={!!errors?.[name]?.['simulate']}
          updateSimulateField={updateSimulateField}
          instrumentName={name}
        />
      )}
      {Object.keys(instrument.config).sort().map(renderField)}
    </InstrumentAccordion>
  );
};
